import React, { useEffect, useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { ConnectorType } from '../types';
import { fuzzyMatch } from '../utils/fuzzy';
import { COLORS } from '../utils/constants';
import { CONNECTOR_TYPE_DND } from './CanvasContainer';
import { SidePanel } from './SidePanel';

interface ConnectorTypePanelProps {
  stateManager: StateManager;
  onEditType: (typeId: string) => void;
  /** Opens the form to define a new type; the type is added only when saved. */
  onCreateType: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  width: number;
  onWidthChange: (width: number) => void;
}

/**
 * Left-side panel listing the connector-type catalog. A type can be dragged onto
 * the canvas to place a connector of that type, or clicked to edit it; the list
 * can be fuzzy-searched. The button at the bottom opens the form for a new type.
 */
export const ConnectorTypePanel: React.FC<ConnectorTypePanelProps> = ({
  stateManager,
  onEditType,
  onCreateType,
  collapsed,
  onToggleCollapsed,
  width,
  onWidthChange
}) => {
  const [types, setTypes] = useState<ConnectorType[]>(() => stateManager.getState().connectorTypes);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const update = () => setTypes([...stateManager.getState().connectorTypes]);
    update();
    return stateManager.subscribe(update);
  }, [stateManager]);

  const visibleTypes = types.filter(type => fuzzyMatch(search, `${type.name} ${type.note ?? ''}`));

  return (
    <SidePanel
      side="left"
      title="Connector Types"
      count={types.length}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      width={width}
      onWidthChange={onWidthChange}
    >
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search types..."
          style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ overflowY: 'auto', flex: 1 }}>
        {types.length === 0 && (
          <div style={{ padding: '16px', color: '#b2bec3', fontSize: '13px' }}>
            No connector types yet. Create one below, then drag it onto the canvas.
          </div>
        )}

        {types.length > 0 && visibleTypes.length === 0 && (
          <div style={{ padding: '16px', color: '#b2bec3', fontSize: '13px' }}>
            No types match "{search}".
          </div>
        )}

        {visibleTypes.map(type => (
          <div
            key={type.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(CONNECTOR_TYPE_DND, type.id);
              e.dataTransfer.effectAllowed = 'copy';
            }}
            onClick={() => onEditType(type.id)}
            title="Drag onto the canvas to place, or click to edit"
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid #f4f4f4',
              cursor: 'grab',
              userSelect: 'none'
            }}
          >
            <div style={{ fontSize: '14px', color: '#2d3436', fontWeight: 600 }}>{type.name}</div>
            <div style={{ fontSize: '12px', color: '#636e72', marginTop: '2px' }}>
              {type.pins.length} pin{type.pins.length === 1 ? '' : 's'}
            </div>
            {type.note && (
              <div style={{ fontSize: '12px', color: '#b2bec3', marginTop: '2px' }}>{type.note}</div>
            )}
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid #eee' }}>
        <button
          onClick={onCreateType}
          style={{ width: '100%', padding: '8px 12px', border: 'none', borderRadius: '4px', backgroundColor: COLORS.ACTION, color: 'white', cursor: 'pointer', fontSize: '13px' }}
        >
          + New Type
        </button>
      </div>
    </SidePanel>
  );
};
